// The ONE source of Symphony agent-task bucketing, mirroring native's `AgentTaskBucket`
// (native-apps/ios/Symphony/Navigation/AppRouter.swift) 1:1. Every count that fronts a tab — the
// workspace "Agents at work" stat cards AND the Agent Tasks tabs — must come from here.
//
// Native shipped, and fixed, the same class of bug this replaces: hand-rolled per-surface bucketing
// filed FAILED/CANCELLED under In progress and skipped the recency window, so a stat card promised a
// count the tab it opened could not show.
//
// Status-string compared (not enum) so both the raw `AgentTask` (workspace card) and the widened
// `WebTask` (`DigestedTaskStatus | AgentTaskStatus`) fall through cleanly.

export type AgentTaskBucket = 'waiting' | 'inProgress' | 'done' | 'failed';

/**
 * Every bucket, in tab order. This is the addressable set (`?tab=`, counts, bucketing) — NOT what the
 * strip renders. For that, see `visibleBuckets`.
 */
export const AGENT_TASK_BUCKETS: AgentTaskBucket[] = ['waiting', 'inProgress', 'done', 'failed'];

/** The three buckets that are always rendered — `failed` is the exceptional one and joins conditionally. */
const ALWAYS_VISIBLE_BUCKETS: AgentTaskBucket[] = ['waiting', 'inProgress', 'done'];

/**
 * The bucket's BI token — what a tab-switch click reports as its `entity_id`.
 *
 * NOT the same string as the TS union: web spells the middle bucket `inProgress` (a camelCase
 * identifier) while the shared BI vocabulary is `in-progress`, which is what the native apps'
 * `AgentTaskBucket.slug` emits and what the route arg carries. The tab funnel only joins across
 * web/iOS/Android if all three send the identical token, so this mapping is the seam — never
 * report the raw union value. Pinned by `test/bi-entity-parity.test.ts`.
 */
export const AGENT_TASK_BUCKET_BI_SLUG: Record<AgentTaskBucket, string> = {
  waiting: 'waiting',
  inProgress: 'in-progress',
  done: 'done',
  failed: 'failed',
};

/** The minimum an entity needs to be bucketed. Satisfied by both `AgentTask` and `WebTask`. */
export interface BucketableTask {
  status?: string;
  updatedDate?: string | Date;
  // Is an agent loop executing on this task RIGHT NOW? Both names carry the same verdict, computed by the
  // same rule (`taskRunState` in common-lib, off the conversation's persisted `pendingPredictionId`); they
  // differ only in which payload stamped it. `runInFlight` is the LIST's field (the `tasks` bridge action
  // and `/api/tasks`); `isLive` is the older SINGLE-task field, kept because the task-detail read and both
  // native clients already consume that name. Absent on a payload that computed neither.
  runInFlight?: boolean;
  isLive?: boolean;
}

/**
 * Which bucket a status files under. Mirrors native `AgentTaskBucket.containing`:
 * ACTION_NEEDED and PAUSED → waiting (Action needed); COMPLETED / CANCELLED → done (both terminal);
 * FAILED → its own bucket (terminal, but must not read as a successful "Done"); everything else —
 * including PENDING, IN_PROGRESS, WAITING, IN_REVIEW and any unknown — → inProgress.
 *
 * PENDING is deliberately inProgress, not waiting: a queued task is the agent's turn, not the user's.
 */
export function bucketContaining(status: string | undefined): AgentTaskBucket {
  switch (status) {
    case 'ACTION_NEEDED':
    case 'PAUSED':
      return 'waiting';
    case 'COMPLETED':
    case 'CANCELLED':
      return 'done';
    case 'FAILED':
      return 'failed';
    default:
      return 'inProgress';
  }
}

export function bucketOfTask(task: BucketableTask): AgentTaskBucket {
  return bucketContaining(task.status);
}

/**
 * Whether a task has a live agent loop executing RIGHT NOW — the island's blue "in motion" signal, and
 * the task-detail spinner. Prefer whichever run-state field the payload carries and fall back to the
 * `inProgress` bucket only when it carries neither, so a payload that never computed run state can't
 * regress. The point of the signal is that a PAUSED / queued-`PENDING` / dead-loop task — all of which
 * bucket as `inProgress` — is NOT in motion once the real verdict is present.
 *
 * **Reading BOTH names is load-bearing, not defensive clutter.** The list stamps `runInFlight` and the
 * single-task read stamps `isLive`; checking only the older one is how the island kept counting a task
 * that had been `IN_PROGRESS` with nobody on it for seventeen days — the list carried the correct answer
 * under the other name and this function never looked at it.
 */
export function isInMotion(task: BucketableTask): boolean {
  if (typeof task.runInFlight === 'boolean') {
    return task.runInFlight;
  }
  if (typeof task.isLive === 'boolean') {
    return task.isLive;
  }
  return bucketOfTask(task) === 'inProgress';
}

/** Terminal-with-a-problem: the `failed` bucket. Surfaced on its own Failed tab, NOT in the island's
 *  waiting count — a finished task is not waiting on the user, and nothing they do could clear it. */
export function isFailed(task: BucketableTask): boolean {
  return bucketOfTask(task) === 'failed';
}

/** The recency window every agent-task surface shares — native `AgentTaskBucket.recentWindow`. */
export const RECENT_WINDOW_MS = 30 * 86400 * 1000;

/**
 * Tasks updated within the last 30 days. A missing/unparseable `updatedDate` counts as recent, so a
 * task can never be hidden by absent metadata. `now` is injectable for tests.
 */
export function recentTasks<T extends BucketableTask>(tasks: T[], now: number = Date.now()): T[] {
  const cutoff = now - RECENT_WINDOW_MS;
  return tasks.filter((task) => {
    if (!task.updatedDate) return true;
    const updated = new Date(task.updatedDate).getTime();
    return Number.isNaN(updated) || updated >= cutoff;
  });
}

/** The windowed tasks split by bucket — the shape both surfaces render from. */
export function bucketTasks<T extends BucketableTask>(
  tasks: T[],
  now: number = Date.now(),
): Record<AgentTaskBucket, T[]> {
  const byBucket: Record<AgentTaskBucket, T[]> = { waiting: [], inProgress: [], done: [], failed: [] };
  for (const task of recentTasks(tasks, now)) byBucket[bucketOfTask(task)].push(task);
  return byBucket;
}

export function countInBucket(tasks: BucketableTask[], bucket: AgentTaskBucket, now: number = Date.now()): number {
  return recentTasks(tasks, now).filter((task) => bucketOfTask(task) === bucket).length;
}

/**
 * Whether the task has finished, one way or the other — `done` (COMPLETED / CANCELLED) or `failed`.
 * The complement is work the agents are still holding: `waiting` (the user's turn) and `inProgress`.
 */
export function isSettled(task: BucketableTask): boolean {
  const bucket = bucketOfTask(task);
  return bucket === 'done' || bucket === 'failed';
}

/** `updatedDate` as a sortable number. Missing or unparseable sorts last, never crashes the comparator. */
function updatedMs(task: BucketableTask): number {
  if (!task.updatedDate) return 0;
  const ms = new Date(task.updatedDate).getTime();
  return Number.isNaN(ms) ? 0 : ms;
}

/**
 * The order the unified task list is read in: **work the agents are still holding comes before work that
 * has finished**, newest-updated first within each half.
 *
 * Sorting on `updatedDate` alone — which every surface used to do — ranks a task by when it last changed,
 * and finishing IS a change. So a task that completed ten minutes ago outranks one that has been running
 * for two hours, and a long-running or blocked task is pushed off the top of the screen by the very work
 * that is already over. Observed on a real account: an island reading "3 tasks in progress" above six rows
 * every one of which said Done, because the three live ones had sorted below the fold.
 *
 * Deliberately a two-way split, not a per-bucket ranking: `waiting` and `inProgress` are not ordered
 * against each other, because which of "the agent is working" and "the agent needs you" is more urgent is
 * a question about the task, not about its bucket — and the status tabs already separate them for anyone
 * who wants one or the other. Recency decides within each half, as before.
 *
 * Liveness deliberately plays NO part here. Whether a run is in flight is a property that flickers on a
 * ~minute timescale; letting it drive position would make rows jump between places under the reader's
 * cursor mid-scroll. It belongs on the row, as a marker, not in the ordering.
 *
 * ⚠️ **NOT yet ported — this is a live three-surface gap, not a shared rule.** Both phones still sort on
 * `updatedDate` alone (`AgentTasksScreen.swift` `flatByUpdated`, `AgentTasks.kt` `flatTasksByUpdated`), so
 * the defect above is fixed on web and still shipping on iOS and Android. Porting it means mirroring this
 * comparator in both and testing all three against identical fixtures.
 */
export function compareForTaskList(a: BucketableTask, b: BucketableTask): number {
  const aSettled = isSettled(a);
  if (aSettled !== isSettled(b)) {
    return aSettled ? 1 : -1;
  }
  return updatedMs(b) - updatedMs(a);
}

/**
 * The windowed task list in reading order (see {@link compareForTaskList}). Copies before sorting — the
 * caller's array is very often a React Query cache entry, and sorting one in place mutates shared state.
 */
export function sortedForTaskList<T extends BucketableTask>(tasks: T[], now: number = Date.now()): T[] {
  return [...recentTasks(tasks, now)].sort(compareForTaskList);
}

/** A task that can be attributed to the routine that raised it. */
export interface GroupableTask extends BucketableTask {
  /** The routine (pulse identity) whose firing created this task; absent for one-off work. */
  pulseIdentityId?: string;
}

/** One row of the rendered list: either a single task, or consecutive runs of one routine collapsed. */
export interface TaskListRow<T extends GroupableTask> {
  /** Set only on a collapsed row — the routine every task in it came from. */
  pulseIdentityId?: string;
  /** The tasks this row stands for. Length 1 for an ordinary task row. */
  tasks: T[];
  /** Tasks in the row the user still has to deal with. Now that a group is single-outcome this equals
   *  the run count on a `waiting` group and zero on every other, but it stays because the collapsed
   *  header reads it and the native ports mirror the field. */
  needsYouCount: number;
  /** The outcome every run in a collapsed group shares — what lets the header say "7 Done" rather than
   *  an unlabelled "7 runs". Absent on an ordinary single-task row. */
  bucket?: AgentTaskBucket;
}

/**
 * How many consecutive runs of one routine it takes before they collapse into a single row. Two rows of
 * the same routine read fine; it is the eighth that buries the rest of the screen.
 */
export const ROUTINE_RUN_GROUP_THRESHOLD = 3;

/**
 * Fold consecutive runs of the same routine into one row, leaving everything else untouched.
 *
 * A routine that fires hourly produces an hourly task, and that is CORRECT — the work genuinely happened
 * and the record of it is the truth. Observed on a real account: one hourly monitor produced 15 tasks in a
 * day, each reporting a real check of 757 entities. The defect was never that those rows existed, it was
 * that they pushed the day's two pieces of one-off work off the screen. So this collapses the display and
 * changes nothing about what is stored: every run is still there, one expand away.
 *
 * **Consecutive, deliberately.** A one-off task landing between two routine runs breaks the group rather
 * than being swallowed by it, so nothing can hide inside a fold it does not belong to. Grouping is by
 * routine alone — a run's outcome does not split it — and `needsYouCount` is what keeps that safe: a run
 * that needs the user is counted on the header rather than buried.
 *
 * Wired on web by `TasksBody`, which folds the FINAL rendered list (after filtering, tab selection and
 * ordering — "consecutive" means nothing before that) and draws a collapsed row as `RoutineRunGroup`.
 * iOS and Android carry the same fold on their own task lists — the threshold and the consecutive rule
 * are one product rule across the three surfaces, so a change here changes all three.
 */
export function groupRoutineRuns<T extends GroupableTask>(
  tasks: T[],
  threshold: number = ROUTINE_RUN_GROUP_THRESHOLD,
): TaskListRow<T>[] {
  const rows: TaskListRow<T>[] = [];
  const flush = (run: T[]) => {
    const routineId = run[0]?.pulseIdentityId;
    const collapse = run.length >= threshold && !!routineId;
    if (collapse) {
      rows.push({ pulseIdentityId: routineId, tasks: run, needsYouCount: countNeedsYou(run), bucket: bucketOfTask(run[0]) });
      return;
    }
    for (const task of run) rows.push({ tasks: [task], needsYouCount: countNeedsYou([task]) });
  };

  let run: T[] = [];
  for (const task of tasks) {
    // Same routine AND same outcome. Grouping on the routine alone folded a failed run in among six that
    // succeeded, and the collapsed row could then only honestly say "7 runs" — a count with no verdict,
    // which is what made a fold of finished work read as an unlabelled pile. A single-outcome group can
    // state its outcome ("7 Done"), and a run that ended differently gets its own row rather than
    // disappearing into one that misrepresents it.
    const sameRoutine =
      !!task.pulseIdentityId &&
      task.pulseIdentityId === run[0]?.pulseIdentityId &&
      bucketOfTask(task) === bucketOfTask(run[0]);
    if (sameRoutine) {
      run.push(task);
      continue;
    }
    if (run.length > 0) flush(run);
    run = [task];
  }
  if (run.length > 0) flush(run);
  return rows;
}

function countNeedsYou(tasks: BucketableTask[]): number {
  return tasks.filter((task) => bucketOfTask(task) === 'waiting').length;
}

/**
 * The tabs the Agent Tasks strip renders, in order. Waiting / In progress / Done are the primary states
 * and always show even at zero; `failed` is exceptional and shows ONLY when something actually failed
 * inside the window — an empty Failed tab is noise, not information. Mirrors native
 * `AgentTaskBucket.visibleTabs` (native-apps/ios/Symphony/Navigation/AppRouter.swift).
 *
 * Note the window: a failure older than 30 days does not resurrect the tab, since the tab could not show
 * it anyway.
 */
export function visibleBuckets(tasks: BucketableTask[], now: number = Date.now()): AgentTaskBucket[] {
  const visible = [...ALWAYS_VISIBLE_BUCKETS];
  if (countInBucket(tasks, 'failed', now) > 0) visible.push('failed');
  return visible;
}

/**
 * Keeps a selection on a tab that is actually rendered. `failed` can vanish under the user — a refresh
 * that resolves the last failure un-renders the tab they were standing on — so the selection falls back
 * to `waiting` rather than dangling on a strip with nothing highlighted. Mirrors native's
 * `if !visibleTabs.contains(tab) { tab = .waiting }`.
 */
export function clampToVisibleBucket(
  bucket: AgentTaskBucket,
  tasks: BucketableTask[],
  now: number = Date.now(),
): AgentTaskBucket {
  return visibleBuckets(tasks, now).includes(bucket) ? bucket : 'waiting';
}

/**
 * The bucket a surface should open on: an explicit pick passes through (clamped to a rendered tab, so a
 * stale `?tab=failed` can't select a hidden one); anything else resolves to the first non-empty bucket so
 * a generic "see all tasks" entry lands on a populated tab rather than an empty "Waiting on you".
 * Mirrors native `firstNonEmpty` / `resolvedInitialTab`.
 */
export function resolveInitialBucket(
  requested: AgentTaskBucket | undefined,
  tasks: BucketableTask[],
  now: number = Date.now(),
): AgentTaskBucket {
  if (requested) return clampToVisibleBucket(requested, tasks, now);
  return visibleBuckets(tasks, now).find((bucket) => countInBucket(tasks, bucket, now) > 0) ?? 'waiting';
}

/** Parses a `?tab=` query value into a bucket; undefined when absent or unrecognised. */
export function parseBucketParam(value: unknown): AgentTaskBucket | undefined {
  return AGENT_TASK_BUCKETS.find((bucket) => bucket === value);
}

/** Whether a settled task set has nothing at all in the window — drives the empty state. */
export function agentTasksAreEmpty(tasks: BucketableTask[], now: number = Date.now()): boolean {
  return recentTasks(tasks, now).length === 0;
}

export type IslandColor = 'green' | 'blue' | 'orange';

/**
 * The island's colour from the two counts. In-motion (blue) wins whenever anything is in motion; orange
 * only when nothing is in motion but something waits; green only when neither. Mirrored 1:1 on native
 * (`islandPillState`), which resolves exactly these three and nothing else.
 */
export function resolveIslandState(counts: { inMotionCount: number; waitingCount: number }): IslandColor {
  if (counts.inMotionCount > 0) return 'blue';
  if (counts.waitingCount > 0) return 'orange';
  return 'green';
}
